// src/js/pwa.ts — PWA install affordances (D-11, D-12)
// Side-effect-only module — no default/named exports (only `export {}` to satisfy TS module scope).
// Imported by src/index.ts as `import './js/pwa'`.
// Module-scope listeners mirror the Phase-124 scroll-lock block in src/index.ts:64-105.
//
// Android path: captures `beforeinstallprompt` (requires Phase-126 SW + fetch handler to fire),
//   stashes the deferred prompt, reveals #pwa-install-btn, wires its click → prompt().
// iOS path:     detects iOS via UA + navigator.standalone; shows #pwa-ios-banner once (localStorage).
// Standalone gate (D-12): hides both affordances when display-mode:standalone or navigator.standalone.
//
// ZERO service-worker code in this file — SW registration is Phase-126 scope.

// Required for `declare global` to work: file must be a TS module (not a script).
export {};

// ── Module-scope state (mirrors _scrollYBeforeLock in src/index.ts:64) ───────
let _deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

// ── beforeinstallprompt ───────────────────────────────────────────────────────
// Fires only when a qualifying SW with fetch handler is active (Phase 126).
// Until then the button stays hidden — no dead-button UX.
window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    _deferredInstallPrompt = e as BeforeInstallPromptEvent;
    const btn = document.querySelector<HTMLElement>('#pwa-install-btn');
    if (btn) btn.hidden = false;
});

// ── appinstalled ──────────────────────────────────────────────────────────────
// Clear the stashed prompt and re-hide the button after successful installation.
window.addEventListener('appinstalled', () => {
    _deferredInstallPrompt = null;
    const btn = document.querySelector<HTMLElement>('#pwa-install-btn');
    if (btn) btn.hidden = true;
});

// ── DOMContentLoaded — wire install button + iOS banner ──────────────────────
document.addEventListener('DOMContentLoaded', () => {
    // Android install button
    const btn = document.querySelector<HTMLButtonElement>('#pwa-install-btn');
    if (btn) {
        // D-12: already installed in standalone mode — keep hidden
        if (window.matchMedia('(display-mode: standalone)').matches) {
            btn.hidden = true;
        } else {
            btn.addEventListener('click', async () => {
                if (!_deferredInstallPrompt) return;
                _deferredInstallPrompt.prompt();
                const result = await _deferredInstallPrompt.userChoice;
                _deferredInstallPrompt = null;
                if (result.outcome === 'accepted') btn.hidden = true;
            });
        }
    }

    // iOS path — beforeinstallprompt never fires on iOS (RESEARCH Pitfall 2)
    // Show the Share→Add-to-Home-Screen banner once, unless already dismissed or standalone.
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = (navigator as any).standalone === true;  // iOS standalone flag (D-12)
    const dismissed = localStorage.getItem('pwa-ios-banner-dismissed');

    if (isIOS && !isStandalone && !dismissed) {
        const banner = document.querySelector<HTMLElement>('#pwa-ios-banner');
        if (banner) banner.hidden = false;
    }

    // Dismiss button — persist to localStorage so banner only shows once
    document.querySelector('#pwa-ios-banner-dismiss')?.addEventListener('click', () => {
        localStorage.setItem('pwa-ios-banner-dismissed', '1');
        const banner = document.querySelector<HTMLElement>('#pwa-ios-banner');
        if (banner) banner.hidden = true;
    });
});

// ── TypeScript global declarations ────────────────────────────────────────────
// BeforeInstallPromptEvent is not in the standard lib; declare it so tsc is happy.
declare global {
    interface BeforeInstallPromptEvent extends Event {
        prompt(): Promise<void>;
        readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
    }
}
